import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, CreditCard, CheckCircle, GraduationCap, 
  Search, Filter, Plus, Eye, Printer, Phone,
  Calendar, Building, ArrowRight, RefreshCw,
  FileText, Clock, AlertCircle, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PaymentBreakdown from '@/components/PaymentBreakdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdmissionHub = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  // Data States
  const [seatBookings, setSeatBookings] = useState([]);
  const [onlineApplications, setOnlineApplications] = useState([]);
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({
    newLeads: 0,
    paymentPending: 0,
    readyToConvert: 0,
    totalStudents: 0
  });
  
  // UI States
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Server-side Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalBookings, setTotalBookings] = useState(0);
  const [totalApplications, setTotalApplications] = useState(0);
  // Payment Modal States
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_modes: [],
    remarks: ''
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []); // Fetch once on mount, pagination is client-side

  // Helper function to make API call with timeout
  const fetchWithTimeout = async (url, options, timeoutMs = 60000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await axios.get(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.error(`Request timeout for ${url}`);
      }
      throw error;
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    
    // Safety timeout - ensure loading is set to false after max 90 seconds
    const safetyTimeout = setTimeout(() => {
      console.error('Safety timeout triggered - forcing loading to false');
      setLoading(false);
      toast.error('Loading took too long. Please refresh the page.');
    }, 90000);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch ALL data at once (images are excluded so it's fast)
      // Use high limit to get all records for client-side pagination
      const [bookingsRes, applicationsRes, coursesRes, studentsRes] = await Promise.all([
        fetchWithTimeout(`${API_URL}/api/seat-booking/?page=1&limit=1000`, { headers }, 60000)
          .catch(err => {
            console.error('Seat booking fetch error:', err?.response?.data || err.message);
            return { data: { data: [], total: 0 } };
          }),
        fetchWithTimeout(`${API_URL}/api/online-admissions/all?page=1&limit=1000`, { headers }, 60000)
          .catch(err => {
            console.error('Online admissions fetch error:', err?.response?.data || err.message);
            return { data: { data: [], total: 0 } };
          }),
        fetchWithTimeout(`${API_URL}/api/master-settings/courses`, { headers }, 60000)
          .catch(err => {
            console.error('Courses fetch error:', err?.response?.data || err.message);
            return { data: [] };
          }),
        fetchWithTimeout(`${API_URL}/api/students/?page=1&limit=1`, { headers }, 60000)
          .catch(err => {
            console.error('Students fetch error:', err?.response?.data || err.message);
            return { data: [] };
          })
      ]);

      // Clear safety timeout since we got responses
      clearTimeout(safetyTimeout);

      // Handle both old format (array) and new format ({data, total, stats})
      // Note: Global axios interceptor may unwrap {data: [], total, stats} to just []
      // with pagination metadata in response.pagination
      let bookings = [];
      let bookingsTotal = 0;
      let bookingStats = null;
      if (bookingsRes?.data) {
        if (Array.isArray(bookingsRes.data)) {
          bookings = bookingsRes.data;
          // Check if interceptor moved pagination to response.pagination
          bookingsTotal = bookingsRes.pagination?.total ?? bookingsRes.data.length;
          bookingStats = bookingsRes.pagination?.stats || null;
        } else if (bookingsRes.data.data) {
          bookings = Array.isArray(bookingsRes.data.data) ? bookingsRes.data.data : [];
          bookingsTotal = bookingsRes.data.total || bookings.length;
          bookingStats = bookingsRes.data.stats || null;
        }
      }

      let applications = [];
      let applicationsTotal = 0;
      let applicationStats = null;
      if (applicationsRes?.data) {
        if (Array.isArray(applicationsRes.data)) {
          applications = applicationsRes.data;
          applicationsTotal = applicationsRes.pagination?.total ?? applicationsRes.data.length;
          applicationStats = applicationsRes.pagination?.stats || null;
        } else if (applicationsRes.data.data) {
          applications = Array.isArray(applicationsRes.data.data) ? applicationsRes.data.data : [];
          applicationsTotal = applicationsRes.data.total || applications.length;
          applicationStats = applicationsRes.data.stats || null;
        }
      }

      const coursesData = Array.isArray(coursesRes?.data) ? coursesRes.data : [];
      
      // Handle students response - interceptor unwraps {data: [], total} to just []
      // The actual total count is in response.pagination.total
      let studentsTotal = 0;
      if (studentsRes?.data) {
        if (studentsRes.pagination?.total !== undefined) {
          studentsTotal = studentsRes.pagination.total;
        } else if (Array.isArray(studentsRes.data)) {
          studentsTotal = studentsRes.data.length;
        } else if (studentsRes.data.total !== undefined) {
          studentsTotal = studentsRes.data.total;
        }
      }

      console.log('Admission Hub data loaded:', {
        bookings: bookings.length,
        bookingsTotal,
        applications: applications.length,
        applicationsTotal,
        courses: coursesData.length,
        studentsTotal
      });

      setSeatBookings(bookings);
      setOnlineApplications(applications);
      setCourses(coursesData);
      setTotalBookings(bookingsTotal);
      setTotalApplications(applicationsTotal);

      // ========== USE ACCURATE STATS FROM BACKEND ==========
      // These stats are calculated from the ENTIRE database, not just current page
      const paymentPending = bookingStats?.payment_pending ?? bookings.filter(b => 
        b.status === 'Payment Pending' || b.student_status === 'payment_pending'
      ).length;

      const readyToConvert = bookingStats?.ready_to_convert ?? bookings.filter(b => 
        b.status === 'Booked'
      ).length;

      const newLeads = applicationStats?.new_leads ?? applications.filter(a => 
        a.status === 'Pending' && !a.seat_booking_id
      ).length;

      const totalStudentsCount = bookingStats?.total_students ?? studentsTotal;

      setStats({
        newLeads: newLeads,
        paymentPending: paymentPending,
        readyToConvert: readyToConvert,
        totalStudents: totalStudentsCount
      });

    } catch (error) {
      clearTimeout(safetyTimeout);
      console.error('Error fetching data:', error);
      toast.error('Failed to load data. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  };

  const getCourseName = (courseId, item = null) => {
    // First check if course_name is directly available in the item
    if (item?.course_name) {
      return item.course_name;
    }
    // Then try to find from courses array
    const course = courses.find(c => c.id === courseId);
    return course?.name || course?.course_name || 'N/A';
  };

  // Combine and filter data
  const getCombinedData = () => {
    let combined = [];

    // Add seat bookings (excluding converted)
    seatBookings.forEach(booking => {
      if (booking.status !== 'Converted') {
        combined.push({
          id: booking.id,
          type: 'booking',
          name: booking.student_name || `${booking.first_name || ''} ${booking.last_name || ''}`.trim(),
          email: booking.email,
          phone: booking.phone,
          course_id: booking.course_id,
          session: booking.session,
          amount: booking.booking_amount || booking.amount || 0,
          source: booking.source === 'online_form' ? 'Online Form' : 'Walk-in',
          status: getStatus(booking),
          statusColor: getStatusColor(booking),
          date: booking.created_at || booking.booking_date,
          booking_id: booking.booking_id,
          online_application_number: booking.online_application_number,
          raw: booking
        });
      }
    });

    // Add pending online applications (not yet in seat booking)
    onlineApplications.forEach(app => {
      if (app.status === 'Pending' && !app.seat_booking_id) {
        combined.push({
          id: app.id,
          type: 'application',
          name: app.full_name || `${app.first_name || ''} ${app.last_name || ''}`.trim(),
          email: app.email,
          phone: app.phone,
          course_id: app.course_id,
          course_name: app.course_name,
          session: app.session,
          amount: app.amount || 0,
          source: 'Online Form',
          status: 'New Lead',
          statusColor: 'bg-blue-100 text-blue-800',
          date: app.created_at,
          application_number: app.application_number,
          raw: app
        });
      }
    });

    // Sort by date (newest first)
    combined.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Apply filters
    if (activeTab !== 'all') {
      combined = combined.filter(item => {
        switch (activeTab) {
          case 'payment_pending':
            return item.status === 'Payment Pending';
          case 'ready':
            return item.status === 'Ready to Convert';
          case 'new':
            return item.status === 'New Lead';
          default:
            return true;
        }
      });
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      combined = combined.filter(item =>
        item.name?.toLowerCase().includes(search) ||
        item.email?.toLowerCase().includes(search) ||
        item.phone?.includes(search) ||
        item.booking_id?.toLowerCase().includes(search) ||
        item.application_number?.toLowerCase().includes(search)
      );
    }

    return combined;
  };

  const getStatus = (booking) => {
    if (booking.status === 'Payment Pending' || booking.student_status === 'payment_pending') {
      return 'Payment Pending';
    }
    // Only show "Ready to Convert" for Booked status (not Converted)
    if (booking.status === 'Booked') {
      return 'Ready to Convert';
    }
    // Converted means already admitted - show as Converted
    if (booking.status === 'Converted') {
      return 'Converted';
    }
    return booking.status;
  };

  const getStatusColor = (booking) => {
    const status = getStatus(booking);
    switch (status) {
      case 'Payment Pending':
        return 'bg-orange-100 text-orange-800';
      case 'Ready to Convert':
        return 'bg-green-100 text-green-800';
      case 'Booked':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Actions
  const handleConvert = (item) => {
    if (item.type === 'booking') {
      navigate(`/students/admission?booking=${item.id}`);
    } else {
      // For online applications, navigate to StudentAdmission with application ID
      navigate(`/students/admission?online_application=${item.id}`);
    }
  };

  const handleCollectPayment = (item) => {
    setSelectedItem(item);
    setPaymentData({
      amount: item.amount || 0,
      payment_modes: [],
      remarks: ''
    });
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async () => {
    if (!selectedItem) return;

    if (!paymentData.payment_modes || paymentData.payment_modes.length === 0) {
      toast.error('Please add at least one payment mode');
      return;
    }

    const totalPaid = paymentData.payment_modes.reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
    if (Math.abs(totalPaid - paymentData.amount) > 0.01) {
      toast.error(`Payment total must equal booking amount`);
      return;
    }

    setPaymentLoading(true);
    try {
      const receiptNo = `SB${Date.now().toString().slice(-8)}`;

      await axios.put(`${API_URL}/api/seat-booking/${selectedItem.id}`, {
        status: 'Booked',
        student_status: 'form_submitted',
        payment_status: 'Paid',
        payment_modes: paymentData.payment_modes,
        amount: paymentData.amount,
        booking_amount: paymentData.amount,
        payment_remarks: paymentData.remarks,
        payment_date: new Date().toISOString().split('T')[0],
        receipt_no: receiptNo,
        collect_payment: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Payment collected successfully!');
      setShowPaymentModal(false);
      setSelectedItem(null);
      fetchAllData();
    } catch (error) {
      console.error('Error collecting payment:', error);
      toast.error('Failed to collect payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleQuickAdd = () => {
    navigate('/seat-booking/book');
  };

  const handleViewDetails = (item) => {
    if (item.type === 'booking') {
      navigate(`/seat-booking/book?edit=${item.id}`);
    }
  };

  const handleDelete = async (item) => {
    const confirmMsg = item.type === 'booking' 
      ? `Are you sure you want to delete this seat booking for "${item.name}"?`
      : `Are you sure you want to delete this online application for "${item.name}"?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (item.type === 'booking') {
        await axios.delete(`${API_URL}/api/seat-booking/${item.id}`, { headers });
        setSeatBookings(prev => prev.filter(b => b.id !== item.id));
        toast.success('Seat booking deleted successfully');
      } else {
        await axios.delete(`${API_URL}/api/online-admissions/${item.id}`, { headers });
        setOnlineApplications(prev => prev.filter(a => a.id !== item.id));
        toast.success('Online application deleted successfully');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const combinedData = getCombinedData();
  
  // Client-side pagination - data is fully loaded, paginate locally
  const totalRecords = combinedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalRecords);
  
  // Slice the data for current page
  const paginatedData = combinedData.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, itemsPerPage]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admission Hub</h1>
          <p className="text-sm text-gray-500">Manage all admissions from one place</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleQuickAdd}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Quick Add
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div 
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveTab('new')}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-blue-100 text-sm">New Leads</p>
              <p className="text-3xl font-bold mt-1">{stats.newLeads}</p>
            </div>
            <div className="bg-blue-400/30 p-2 rounded-lg">
              <FileText className="h-6 w-6" />
            </div>
          </div>
          <p className="text-blue-100 text-xs mt-2">Online applications pending</p>
        </div>

        <div 
          className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveTab('payment_pending')}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-orange-100 text-sm">Payment Pending</p>
              <p className="text-3xl font-bold mt-1">{stats.paymentPending}</p>
            </div>
            <div className="bg-orange-400/30 p-2 rounded-lg">
              <CreditCard className="h-6 w-6" />
            </div>
          </div>
          <p className="text-orange-100 text-xs mt-2">Awaiting payment collection</p>
        </div>

        <div 
          className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveTab('ready')}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-green-100 text-sm">Ready to Convert</p>
              <p className="text-3xl font-bold mt-1">{stats.readyToConvert}</p>
            </div>
            <div className="bg-green-400/30 p-2 rounded-lg">
              <CheckCircle className="h-6 w-6" />
            </div>
          </div>
          <p className="text-green-100 text-xs mt-2">Payment done, ready for admission</p>
        </div>

        <div 
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/students/list')}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-purple-100 text-sm">Total Students</p>
              <p className="text-3xl font-bold mt-1">{stats.totalStudents}</p>
            </div>
            <div className="bg-purple-400/30 p-2 rounded-lg">
              <GraduationCap className="h-6 w-6" />
            </div>
          </div>
          <p className="text-purple-100 text-xs mt-2">Active admitted students</p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          {/* Tabs */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: 'All', count: combinedData.length },
              { key: 'payment_pending', label: 'Payment Pending', count: stats.paymentPending },
              { key: 'ready', label: 'Ready to Convert', count: stats.readyToConvert },
              { key: 'new', label: 'New Leads', count: stats.newLeads }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
                {tab.key !== 'all' && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key ? 'bg-blue-500' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-gray-500">Loading data...</p>
            <p className="mt-1 text-xs text-gray-400">This may take up to 60 seconds for large datasets</p>
          </div>
        ) : combinedData.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-gray-300" />
            <p className="mt-2 text-gray-500">No records found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-blue-600">
                        {item.booking_id || item.application_number || `#${startIndex + index + 1}`}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{item.email}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {item.phone}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getCourseName(item.course_id, item)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{item.session || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        ₹{(item.amount || 0).toLocaleString('en-IN')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        item.source === 'Online Form' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${item.statusColor}`}>
                        {item.status === 'Payment Pending' && <Clock className="h-3 w-3 mr-1" />}
                        {item.status === 'Ready to Convert' && <CheckCircle className="h-3 w-3 mr-1" />}
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        {/* Payment Pending - Show Collect Payment */}
                        {item.status === 'Payment Pending' && (
                          <button
                            onClick={() => handleCollectPayment(item)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm font-medium"
                          >
                            <CreditCard className="h-4 w-4" />
                            Collect
                          </button>
                        )}

                        {/* Ready to Convert - Show Convert */}
                        {item.status === 'Ready to Convert' && (
                          <button
                            onClick={() => handleConvert(item)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium"
                          >
                            <ArrowRight className="h-4 w-4" />
                            Convert
                          </button>
                        )}

                        {/* New Lead - Show Approve */}
                        {item.status === 'New Lead' && (
                          <button
                            onClick={() => handleConvert(item)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium"
                          >
                            <Eye className="h-4 w-4" />
                            Review
                          </button>
                        )}

                        {/* View Details */}
                        {item.type === 'booking' && (
                          <button
                            onClick={() => handleViewDetails(item)}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {!loading && totalRecords > 0 && (
          <div className="px-6 py-4 border-t bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
              <span className="ml-4">
                Showing {totalRecords > 0 ? startIndex + 1 : 0} to {endIndex} of {totalRecords} entries
              </span>
            </div>
            
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {/* Page numbers - only show pages that exist */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    // Only render if pageNum is valid
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                💳 Collect Payment
              </h3>

              {/* Student Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg mb-4 border border-blue-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-base font-semibold text-gray-900">{selectedItem.name}</p>
                    <p className="text-sm text-gray-600">{selectedItem.email}</p>
                    <p className="text-sm text-gray-600">📞 {selectedItem.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Booking ID</p>
                    <p className="text-sm font-bold text-blue-600">{selectedItem.booking_id}</p>
                  </div>
                </div>
                {selectedItem.online_application_number && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <p className="text-xs text-blue-700">
                      🌐 Online Application: <span className="font-semibold">{selectedItem.online_application_number}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Course & Amount */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Course</p>
                  <p className="text-sm font-semibold text-gray-900">{getCourseName(selectedItem.course_id, selectedItem)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Session</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedItem.session || 'N/A'}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Booking Amount</p>
                  <p className="text-lg font-bold text-green-700">₹{(paymentData.amount || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>

              {/* Payment Breakdown */}
              <div className="mb-4">
                <PaymentBreakdown
                  totalAmount={paymentData.amount}
                  paymentModes={paymentData.payment_modes}
                  onChange={(modes) => setPaymentData({ ...paymentData, payment_modes: modes })}
                  label="Payment Breakdown *"
                />
              </div>

              {/* Remarks */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks (Optional)
                </label>
                <textarea
                  value={paymentData.remarks}
                  onChange={(e) => setPaymentData({...paymentData, remarks: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedItem(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={paymentLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPayment}
                  disabled={paymentLoading || !paymentData.payment_modes || paymentData.payment_modes.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {paymentLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Collect Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmissionHub;
